// Project.moduleGuesses — the modules a project's description names, by a
// plain, instant rule (the New project box's Modules list; no assistant
// call). "It should cover light, water and sugar" gives Light, Water,
// Sugar. Two forms count as a list of parts:
//   - a sentence with a cue word ("cover", "include", "täcka", "ta upp",
//     ...) followed by two or more items separated by commas, "and", "och",
//     "or", "eller", "&" or ";";
//   - two or more bulleted or numbered lines ("- Light", "2. Water").
// Anything else gives no guesses: a description with no clear parts leaves
// the list as it is.
//
// The description is data, never instructions: nothing here interprets it
// beyond finding the list. Every guess is a plain name, trimmed, its first
// letter capitalised, at most GUESS_WORDS_MAX words and SHORT_TEXT_MAX
// characters (a longer item is not a module name and is dropped, never
// cut), each name once (case-insensitive), at most NEW_PROJECT_MODULES_MAX
// guesses (CL-22). Only the first LONG_TEXT_MAX characters are read.
//
// Complexity: O(n) time and space in the description's length (capped at
// LONG_TEXT_MAX). Every regex is a flat alternation with no nested
// quantifier, so none can backtrack catastrophically.
export { GUESS_WORDS_MAX, guessModules };
import { LONG_TEXT_MAX, NEW_PROJECT_MODULES_MAX, SHORT_TEXT_MAX, } from "../limits.js";
import { mkResourceTitle } from "../resource/state.js";
// An item with more words than this reads as a sentence, not a name.
const GUESS_WORDS_MAX = 6;
// The words that introduce what a project covers (English, Swedish).
const CUE = /\b(?:cover|covers|covering|include|includes|including|täcka|täcker|tar upp|ta upp|innehålla|innehåller|handla om|handlar om)\b\s*:?/i;
// What separates the items of one list (each item is trimmed after).
const SEPARATOR = /,|;|&|\band\b|\bor\b|\boch\b|\beller\b/i;
// A bulleted or numbered line, and its text.
const BULLET = /^\s*(?:[-*•]|\d{1,3}[.)])\s+(\S.*)$/;
const SENTENCE_END = /[.!?\n]/;
const words = (s) => s.split(/\s+/).filter(Boolean).length;
const named = (item) => {
    const trimmed = item.replace(/[.:!?]+$/, "").trim();
    return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1);
};
// The items after the cue in one sentence (none when it is not a list).
const cuedItems = (sentence) => {
    const cue = CUE.exec(sentence);
    if (cue === null)
        return [];
    const items = sentence
        .slice(cue.index + cue[0].length)
        .split(SEPARATOR)
        .filter((s) => s.trim() !== "");
    return items.length >= 2 ? items : [];
};
const bulletItems = (lines) => {
    const items = lines.flatMap((line) => {
        const m = BULLET.exec(line);
        return m?.[1] === undefined ? [] : [m[1]];
    });
    return items.length >= 2 ? items : [];
};
const guessModules = (description) => {
    const text = description.slice(0, LONG_TEXT_MAX);
    const lines = text.split("\n");
    const bulleted = bulletItems(lines);
    const raw = bulleted.length > 0
        ? bulleted
        : text.split(SENTENCE_END).flatMap(cuedItems);
    // Too long is dropped before anything else reads the item.
    const names = raw
        .map((item) => item.trim())
        .filter((item) => item.length <= SHORT_TEXT_MAX)
        .map(named)
        .filter((n) => n !== "" && words(n) <= GUESS_WORDS_MAX);
    const seen = new Set();
    const once = names.filter((n) => {
        const key = n.toLocaleLowerCase();
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
    return once.slice(0, NEW_PROJECT_MODULES_MAX).map(mkResourceTitle);
};
//# sourceMappingURL=moduleGuesses.js.map